// Organizations and users: the two demo orgs (outlet + recycler, so the
// sidebar organization switcher doubles as the persona switcher), the demo
// user, outlet staff, recycler managers and the ten drivers.

import type { UserSchema } from '../../types/auth.type';
import { OrganizationType, type OrganizationSchema } from '../../types/organization.type';
import {
  DEMO_DRIVERS,
  DEMO_OUTLET_ORG,
  DEMO_OUTLET_ORG_ID,
  DEMO_OUTLET_STAFF,
  DEMO_RECYCLER_MANAGERS,
  DEMO_RECYCLER_ORG,
  DEMO_RECYCLER_ORG_ID,
  DEMO_TRUCKS,
  DEMO_USER,
  DEMO_USER_ID,
  daysAgo,
  hashString,
  iso,
  isoDate,
  type DemoDriver,
  type DemoSeedOrganization,
} from '../core';
import { makeCounter, mapsUrl, nowIso } from './common';

export type DemoOrganization = OrganizationSchema & {
  name_ar?: string;
  city?: string;
  country?: string;
  email?: string;
  phone?: string;
  cr_number?: string;
  vat_number?: string;
  logo_url: string | null;
  [key: string]: unknown;
};

function buildOrg(seed: DemoSeedOrganization, lat: number, lng: number, createdDaysAgo: number): DemoOrganization {
  const type = seed.organization_type === 'recycler' ? OrganizationType.RECYCLER : OrganizationType.WASTE_OUTLET;
  return {
    id: seed.id,
    name: seed.name,
    name_ar: seed.name_ar as string,
    organization_type: type,
    google_maps_url: mapsUrl(lat, lng),
    city: seed.city as string,
    country: seed.country as string,
    email: seed.email as string,
    phone: seed.phone as string,
    cr_number: seed.cr_number as string,
    vat_number: seed.vat_number as string,
    logo_url: null,
    data: {
      name_ar: seed.name_ar,
      city: seed.city,
      country: 'Saudi Arabia',
      contact_email: seed.email,
      phone: seed.phone,
      cr_number: seed.cr_number,
      vat_number: seed.vat_number,
      logo_url: null,
      is_pilot: false,
    },
    created_at: iso(daysAgo(createdDaysAgo)),
    updated_at: iso(daysAgo(14)),
  };
}

let organizations: DemoOrganization[] = [];
let users: Record<string, UserSchema[]> = {};
const userCounter = makeCounter(100);

/** The typed organization object DemoEntry seeds as `selectedOrganization`. */
export function demoOrganizationFor(persona: 'outlet' | 'recycler'): DemoOrganization {
  return persona === 'outlet'
    ? buildOrg(DEMO_OUTLET_ORG, 24.7005, 46.68, 540)
    : buildOrg(DEMO_RECYCLER_ORG, 24.64, 46.83, 610);
}

const RESIDENT_NATIONALITY: Record<string, string> = {
  'Muhammad Asif': 'Pakistan',
  'Rashid Khan': 'Pakistan',
  'Sajid Hussain': 'Pakistan',
  'Anil Kumar': 'India',
  'Shafiq Rahman': 'Bangladesh',
};

function iban(seed: string): string {
  let digits = '';
  let h = hashString(seed);
  while (digits.length < 22) {
    h = Math.imul(h ^ (h >>> 13), 0x5bd1e995) >>> 0;
    digits += String(h % 10);
  }
  return `SA${digits}`;
}

function splitName(full: string): { first: string; last: string } {
  const parts = full.split(' ');
  return { first: parts[0], last: parts.slice(1).join(' ') };
}

function demoUserRecord(): UserSchema {
  return {
    id: DEMO_USER_ID,
    sub: DEMO_USER_ID,
    username: DEMO_USER.username,
    email: DEMO_USER.email,
    full_name: DEMO_USER.full_name,
    phone: DEMO_USER.phone,
    is_active: true,
    status: 'active',
    created_at: iso(daysAgo(600)),
    updated_at: iso(daysAgo(3)),
    data: {
      permissions: [`recyclee.${DEMO_OUTLET_ORG_ID}.*`, `recyclee.${DEMO_RECYCLER_ORG_ID}.*`],
      permissions_legacy: {
        [DEMO_OUTLET_ORG_ID]: ['waste_outlet_admin'],
        [DEMO_RECYCLER_ORG_ID]: ['recycler_admin'],
      },
      sites: { [DEMO_OUTLET_ORG_ID]: ['*'], [DEMO_RECYCLER_ORG_ID]: ['*'] },
      full_name: DEMO_USER.full_name,
      first_name: 'Demo',
      last_name: 'User',
      email: DEMO_USER.email,
    },
  };
}

/** Plate of the truck a driver is assigned to in the story (their registration number). */
function driverPlateFor(driverId: string): string | null {
  return DEMO_TRUCKS.find((t) => t.driver_id === driverId)?.plate_number ?? null;
}

function driverUser(driver: DemoDriver, index: number): UserSchema {
  const { first, last } = splitName(driver.full_name);
  const nationality = RESIDENT_NATIONALITY[driver.full_name] ?? 'Saudi Arabia';
  const birthYear = 1978 + (hashString(driver.id) % 22);
  return {
    id: driver.id,
    sub: driver.id,
    username: driver.email.split('@')[0],
    email: driver.email,
    full_name: driver.full_name,
    phone: driver.phone,
    organization_id: DEMO_RECYCLER_ORG_ID,
    is_active: driver.status === 'active',
    status: driver.status === 'active' ? 'active' : 'inactive',
    created_at: iso(daysAgo(520 - index * 31)),
    updated_at: iso(daysAgo(5 + index)),
    data: {
      permissions: [`recyclee.${DEMO_RECYCLER_ORG_ID}.*.route_run.read`, `recyclee.${DEMO_RECYCLER_ORG_ID}.*.assignment.respond`],
      permissions_legacy: { [DEMO_RECYCLER_ORG_ID]: ['recycler_staff'] },
      full_name: driver.full_name,
      full_name_ar: driver.full_name_ar,
      first_name: first,
      last_name: last,
      phone: driver.phone,
      email: driver.email,
      gender: 'Male',
      nationality,
      date_of_birth: `${birthYear}-${String((hashString(driver.id + 'm') % 12) + 1).padStart(2, '0')}-${String((hashString(driver.id + 'd') % 27) + 1).padStart(2, '0')}`,
      iban: iban(driver.id),
      driver_license_number: driver.licence_no,
      driver_license_expiry: driver.licence_expiry,
      vehicle_registration_number: driverPlateFor(driver.id) ?? '',
      city: driver.city,
      home_facility_id: driver.home_facility_id,
      duty_status: driver.status,
      total_points: 120 + (hashString(driver.id) % 380),
    },
  };
}

function managerUsers(): UserSchema[] {
  return DEMO_RECYCLER_MANAGERS.map((m, i) => {
    const { first, last } = splitName(m.full_name);
    return {
      id: m.id,
      sub: m.id,
      username: m.email.split('@')[0],
      email: m.email,
      full_name: m.full_name,
      phone: m.phone,
      organization_id: DEMO_RECYCLER_ORG_ID,
      is_active: true,
      status: 'active',
      created_at: iso(daysAgo(580 - i * 40)),
      updated_at: iso(daysAgo(9)),
      data: {
        permissions: [`recyclee.${DEMO_RECYCLER_ORG_ID}.*`],
        permissions_legacy: { [DEMO_RECYCLER_ORG_ID]: ['recycler_ops_manager'] },
        full_name: m.full_name,
        full_name_ar: m.full_name_ar,
        first_name: first,
        last_name: last,
        phone: m.phone,
        email: m.email,
        job_title: m.job_title,
        facility_id: m.facility_id,
      },
    };
  });
}

function outletStaffUsers(): UserSchema[] {
  return DEMO_OUTLET_STAFF.map((s, i) => {
    const { first, last } = splitName(s.full_name);
    return {
      id: s.id,
      sub: s.id,
      username: s.email.split('@')[0],
      email: s.email,
      full_name: s.full_name,
      phone: s.phone,
      organization_id: DEMO_OUTLET_ORG_ID,
      is_active: true,
      status: 'active',
      created_at: iso(daysAgo(500 - i * 23)),
      updated_at: iso(daysAgo(7 + i)),
      data: {
        permissions: [`recyclee.${DEMO_OUTLET_ORG_ID}.*`],
        permissions_legacy: {
          [DEMO_OUTLET_ORG_ID]: [s.role === 'ops_manager' ? 'waste_outlet_ops_manager' : 'waste_outlet_staff'],
        },
        sites: { [DEMO_OUTLET_ORG_ID]: s.site_ids },
        full_name: s.full_name,
        full_name_ar: s.full_name_ar,
        first_name: first,
        last_name: last,
        phone: s.phone,
        email: s.email,
        job_title: s.job_title,
        job_title_ar: s.job_title_ar,
      },
    };
  });
}

export function resetPeople(): void {
  userCounter.reset();
  organizations = [demoOrganizationFor('outlet'), demoOrganizationFor('recycler')];
  users = {
    [DEMO_OUTLET_ORG_ID]: [demoUserRecord(), ...outletStaffUsers()],
    [DEMO_RECYCLER_ORG_ID]: [demoUserRecord(), ...DEMO_DRIVERS.map(driverUser), ...managerUsers()],
  };
}

// ── Organizations ─────────────────────────────────────────────────────────

export function listOrganizations(): DemoOrganization[] {
  return organizations;
}

export function organizationById(id: string | null | undefined): DemoOrganization | undefined {
  return organizations.find((o) => o.id === id);
}

export function organizationName(id: string | null | undefined): string | null {
  return organizationById(id)?.name ?? null;
}

export function createOrganization(input: Record<string, unknown>): DemoOrganization {
  const id = `00000000-0000-4000-8000-0000a0${String(organizations.length + 1).padStart(6, '0')}`;
  const org: DemoOrganization = {
    id,
    name: String(input.name ?? 'New organization'),
    organization_type: (input.organization_type as OrganizationType) ?? OrganizationType.WASTE_OUTLET,
    google_maps_url: String(input.google_maps_url ?? ''),
    data: (input.data as Record<string, unknown>) ?? {},
    logo_url: null,
    created_at: nowIso(),
    updated_at: nowIso(),
  };
  organizations.push(org);
  return org;
}

export function updateOrganization(id: string, patch: Record<string, unknown>): DemoOrganization | undefined {
  const org = organizationById(id);
  if (!org) return undefined;
  if (typeof patch.name === 'string') org.name = patch.name;
  if (typeof patch.google_maps_url === 'string') org.google_maps_url = patch.google_maps_url;
  if (patch.data && typeof patch.data === 'object') org.data = { ...org.data, ...(patch.data as Record<string, unknown>) };
  org.updated_at = nowIso();
  return org;
}

// ── Users ─────────────────────────────────────────────────────────────────

export function currentUser(): UserSchema {
  return users[DEMO_RECYCLER_ORG_ID]?.find((u) => u.id === DEMO_USER_ID) ?? demoUserRecord();
}

export function usersFor(orgId: string | null | undefined): UserSchema[] {
  return orgId ? users[orgId] ?? [] : [];
}

export function userById(id: string | null | undefined): UserSchema | undefined {
  if (!id) return undefined;
  for (const list of Object.values(users)) {
    const hit = list.find((u) => u.id === id);
    if (hit) return hit;
  }
  return undefined;
}

export function userDisplayName(id: string | null | undefined): string | null {
  const u = userById(id);
  return u ? u.full_name ?? u.username : null;
}

export function updateUser(orgId: string, userId: string, patch: Record<string, unknown>): UserSchema | undefined {
  const user = usersFor(orgId).find((u) => u.id === userId) ?? userById(userId);
  if (!user) return undefined;
  if (typeof patch.username === 'string' && patch.username) user.username = patch.username;
  if (patch.phone !== undefined) user.phone = (patch.phone as string | null) ?? null;
  if (typeof patch.status === 'string') {
    user.status = patch.status;
    user.is_active = patch.status === 'active';
  }
  if (patch.data && typeof patch.data === 'object') {
    user.data = { ...(user.data ?? {}), ...(patch.data as Record<string, unknown>) };
    const d = user.data;
    if (d.first_name || d.last_name) {
      user.full_name = [d.first_name, d.last_name].filter(Boolean).join(' ');
      d.full_name = user.full_name;
    }
  }
  user.updated_at = nowIso();
  return user;
}

export function removeUser(userId: string): boolean {
  let removed = false;
  for (const orgId of Object.keys(users)) {
    const before = users[orgId].length;
    users[orgId] = users[orgId].filter((u) => u.id !== userId);
    removed = removed || users[orgId].length !== before;
  }
  return removed;
}

export function usernameTaken(username: string): boolean {
  const u = username.trim().toLowerCase();
  return Object.values(users).some((list) => list.some((x) => x.username.toLowerCase() === u));
}

/** POST /truck/driver/register: a new recycler_staff user with a driver profile. */
export function registerDriverUser(input: Record<string, unknown>): UserSchema {
  const orgId = String(input.organization_id ?? DEMO_RECYCLER_ORG_ID);
  const n = userCounter.next();
  const id = `00000000-0000-4000-8000-0000d4${n.toString(16).padStart(6, '0')}`;
  const first = String(input.first_name ?? '').trim();
  const last = String(input.last_name ?? '').trim();
  const fullName = [first, last].filter(Boolean).join(' ') || String(input.username ?? 'New driver');
  const user: UserSchema = {
    id,
    sub: id,
    username: String(input.username ?? `driver${n}`),
    full_name: fullName,
    phone: (input.phone as string | null) ?? null,
    organization_id: orgId,
    is_active: true,
    status: 'active',
    created_at: nowIso(),
    updated_at: nowIso(),
    data: {
      permissions: [`recyclee.${orgId}.*.route_run.read`],
      permissions_legacy: { [orgId]: ['recycler_staff'] },
      full_name: fullName,
      first_name: first || null,
      last_name: last || null,
      phone: (input.phone as string | null) ?? null,
      gender: (input.gender as string | null) ?? null,
      nationality: (input.nationality as string | null) ?? null,
      date_of_birth: (input.date_of_birth as string | null) ?? null,
      iban: (input.iban as string | null) ?? null,
      driver_license_number: String(input.driver_license_number ?? ''),
      driver_license_expiry: (input.driver_license_expiry as string | null) ?? null,
      vehicle_registration_number: String(input.vehicle_registration_number ?? ''),
      total_points: Number(input.total_points ?? 0),
    },
  };
  if (!users[orgId]) users[orgId] = [];
  users[orgId].push(user);
  return user;
}

/** Driver licences expiring within `days` (for notifications). */
export function expiringLicences(days: number): DemoDriver[] {
  const limit = isoDate(new Date(Date.now() + days * 86400000));
  return DEMO_DRIVERS.filter((d) => d.licence_expiry <= limit);
}
